import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";

// Shared dialog chrome for every "info"/"edit" popup in the app.
// Replaces the old pattern of a bare <Modal><Box sx={...}></Box></Modal>
// per file (four slightly different hand-rolled looks, one of them with a
// `paperprops` prop that isn't real and silently did nothing). Using a
// real MUI <Dialog> also gets us a working backdrop, Escape-to-close, and
// centered positioning for free, instead of the old absolute-position/
// transform hacks.
//
// Design: a plain white dialog throughout (no colored fill anywhere — a
// solid-colored title bar read as heavy/dated against the rest of the
// app). Brand color shows up as a small accent tab next to the title
// instead, and each section (title / body / actions) is separated by a
// thin neutral divider rather than a block of color, so the eye reads it
// as one clean surface with a clear hierarchy: title, content, actions.
//
// Usage: <AppDialog open={...} onClose={...} title="..." actions={<...>}>
//          {content}
//        </AppDialog>
// `actions` is optional — omit it when the content already renders its
// own buttons (e.g. the viewEdit*.js forms).
const AppDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  contentSx,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            fontSize: "1.05rem",
            fontWeight: 600,
            color: "primary.main",
            py: 2.25,
            px: 3,
            pr: 6,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              borderRadius: 4,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          />
          {title}
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                position: "absolute",
                right: 14,
                top: 14,
                color: "text.secondary",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent sx={{ px: 3, pt: 3.5, pb: 2.5, ...contentSx }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppDialog;
